import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiEye, FiLayers, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { stateManagementAPI } from '../../services/stateManagementAPI';
import './StateManagement.css';

const Districts = () => {
  const navigate = useNavigate();
  const { commissionaryId } = useParams();
  const location = useLocation();
  const commissionaryName = location.state?.commissionaryName || 'Unknown Commissionary';
  const stateName = location.state?.stateName || 'Unknown State';
  
  const [districts, setDistricts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [state, setState] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalMatching: 0,
    totalDistricts: 0
  });
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    headQuarter: ''
  });
const [errors, setErrors] = useState({
  name: '',
  code: '',
  headQuarter: ''
});
const validateForm = () => {
  const tempErrors = {};
  let isValid = true;

  // District Name
  if (!formData.name.trim()) {
    tempErrors.name = 'District name is required';
    isValid = false;
  } else if (formData.name.trim().length < 2) {
    tempErrors.name = 'District name must be at least 2 characters';
    isValid = false;
  }

  // Code
  if (!formData.code.trim()) {
    tempErrors.code = 'Code is required';
    isValid = false;
  } else if (formData.code.trim().length < 2) {
    tempErrors.code = 'Code must be at least 2 characters';
    isValid = false;
  }

  // HeadQuarter
  if (!formData.headQuarter.trim()) {
    tempErrors.headQuarter = 'Headquarter is required';
    isValid = false;
  } else if (formData.headQuarter.trim().length < 2) {
    tempErrors.headQuarter =
      'Headquarter must be at least 2 characters';
    isValid = false;
  }

  setErrors(tempErrors);
  return isValid;
};
  // Fetch districts from API
  const fetchDistricts = async () => {
    try {
      setLoading(true);
      const response = await stateManagementAPI.getDistricts(commissionaryId, currentPage, itemsPerPage, searchTerm);

      if (response.success) {
        setDistricts(response.data || []);
        setPagination(response.pagination || {
          totalPages: 1,
          totalMatching: 0,
          totalDistricts: 0
        });
        setState(response?.pagination?.state?.state);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      toast.error('Failed to fetch districts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    fetchDistricts();

  }, [commissionaryId, currentPage, searchTerm]);

const handleAdd = () => {
  setModalMode('add');
  setFormData({
    name: '',
    code: '',
    headQuarter: ''
  });

  setErrors({
    name: '',
    code: '',
    headQuarter: ''
  });

  setShowModal(true);
};

const handleEdit = (e, district) => {
  e.stopPropagation();

  setModalMode('edit');
  setSelectedDistrict(district);

  setFormData({
    name: district.name,
    code: district.code,
    headQuarter: district.headQuarter
  });

  setErrors({
    name: '',
    code: '',
    headQuarter: ''
  });

  setShowModal(true);
};

  const handleDelete = (e, district) => {
    e.stopPropagation();
    setItemToDelete(district);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await stateManagementAPI.deleteDistrict(itemToDelete._id);
      toast.success('District deleted successfully');
      setShowDeleteModal(false);
      setItemToDelete(null);
      fetchDistricts();
    } catch (error) {
      console.error('Error deleting district:', error);
      toast.error('Failed to delete district');
    } finally {
      setLoading(false);
    }
  };

 const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateForm()) return;

  try {
    setLoading(true);

    if (modalMode === 'add') {
      const payload = {
        ...formData,
        commissionary: commissionaryId
      };

      await stateManagementAPI.createDistrict(payload);
      toast.success('District added successfully');
    } else {
      await stateManagementAPI.updateDistrict(
        selectedDistrict._id,
        formData
      );

      toast.success('District updated successfully');
    }

    setShowModal(false);
    fetchDistricts();
  } catch (error) {
    console.error('Error saving district:', error);
    toast.error(
      `Failed to ${modalMode === 'add' ? 'add' : 'update'} district`
    );
  } finally {
    setLoading(false);
  }
};
  const handleBackToCommissionaries = () => {
    if (state) {
      navigate(`/commissionaries/${state}`, {
        state: { stateName: stateName }
      });
    }

  };

  return (
    <div className="state-management">
      <div className="page-header">
        <div className="header-content">
         {commissionaryId && <div className="breadcrumb">
            <button
              className="back-btn"
              onClick={handleBackToCommissionaries}
            >
              <FiArrowLeft />
            </button>
             <span className="breadcrumb-text">
              States / {stateName} / Commissionaries / {commissionaryName} / Districts
            </span>
          </div>}
          {commissionaryId ? <h1 className="page-title">
            <FiLayers className="title-icon" />
            Districts in {commissionaryName}
          </h1> :
            <h1 className="page-title">
              <FiLayers className="title-icon" />
              All Districts
            </h1>}
          {commissionaryId && <p className="page-subtitle">Manage districts in this commissionary</p>}
        </div>
        {commissionaryId && <button className="add-btn" onClick={handleAdd}>
          <FiPlus /> Add District
        </button>}
      </div>

      {/* Dashboard Cards */}
      <div className="dashboard-cards">
        <div className="dashboard-card">
          <div className="card-content">
            <div className="card-info">
              <h3>Total Districts</h3>
              <div className="card-value">{pagination.totalDistricts}</div>
            </div>
            <div className="card-icon districts">
              <FiLayers />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="controls-section">
        <div className="search-container">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search districts..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
        </div>
      </div>

      {/* Districts Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-container">
            <p>Loading...</p>
          </div>
        ) : (
          <table className="states-table">
            <thead>
              <tr>
                <th>District Name</th>
                <th>Code</th>
                <th>Headquarters</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {districts.length > 0 ? districts.map((district) => (
                <tr key={district._id} className="table-row">
                  <td className="state-name">{district.name}</td>
                  <td>{district.code}</td>
                  <td>{district.headQuarter}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button
                        className="action-btn edit"
                        onClick={(e) => handleEdit(e, district)}
                        title="Edit"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={(e) => handleDelete(e, district)}
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    No districts found!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1 || loading}
        >
          Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {pagination.totalPages}
        </span>
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages))}
          disabled={currentPage === pagination.totalPages || loading}
        >
          Next
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modalMode === 'add' ? 'Add District' : 'Edit District'}
              </h2>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
  <label>District Name</label>

  <input
    type="text"
    value={formData.name}
    onChange={(e) => {
      setFormData({
        ...formData,
        name: e.target.value.slice(0, 50)
      });

      setErrors({
        ...errors,
        name: ''
      });
    }}
  />

  {errors.name && (
    <span className="error-text">{errors.name}</span>
  )}
</div>
           <div className="form-group">
  <label>Code</label>

  <input
    type="text"
    value={formData.code}
    onChange={(e) => {
      setFormData({
        ...formData,
        code: e.target.value.slice(0, 50)
      });

      setErrors({
        ...errors,
        code: ''
      });
    }}
  />

  {errors.code && (
    <span className="error-text">{errors.code}</span>
  )}
</div>
          <div className="form-group">
  <label>Headquarters</label>

  <input
    type="text"
    value={formData.headQuarter}
    onChange={(e) => {
      setFormData({
        ...formData,
        headQuarter: e.target.value.slice(0, 50)
      });

      setErrors({
        ...errors,
        headQuarter: ''
      });
    }}
  />

  {errors.headQuarter && (
    <span className="error-text">{errors.headQuarter}</span>
  )}
</div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={loading}>
                  {loading ? 'Saving...' : (modalMode === 'add' ? 'Add District' : 'Update District')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{itemToDelete?.name}</strong>?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setShowDeleteModal(false)} disabled={loading}>
                Cancel
              </button>
              <button type="button" className="danger" onClick={confirmDelete} disabled={loading}>
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Districts;